import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { MentionableItem } from '../../../../../types'
import { closeMentionsAtom, mentionSearchAtom, selectedMentionIndexAtom } from '../atoms'

/** Arrow keys, plus the emacs-style ctrl+n / ctrl+p the rest of the app accepts. */
const stepFor = (e: KeyboardEvent): number | null => {
  const isCtrlOnly = e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey
  if (e.key === 'ArrowDown' || (e.key === 'n' && isCtrlOnly)) return 1
  if (e.key === 'ArrowUp' || (e.key === 'p' && isCtrlOnly)) return -1
  return null
}

/**
 * Returns a factory rather than a handler: the caller binds it to the current
 * result list, so arrow/enter navigation always wraps against what is on screen.
 */
export const useMentionsKeyboardNavigation = (onSelectMention: (mention: MentionableItem) => void) => {
  const mentionSearch = useAtomValue(mentionSearchAtom)
  const mentionIndex = useAtomValue(selectedMentionIndexAtom)
  const setMentionIndex = useSetAtom(selectedMentionIndexAtom)
  const closeMentions = useSetAtom(closeMentionsAtom)

  return useCallback(
    (combinedData: Array<MentionableItem>) => {
      if (!combinedData.length) {
        return () => {}
      }

      return (e: KeyboardEvent) => {
        const step = stepFor(e)
        if (step !== null) {
          e.stopImmediatePropagation()
          e.preventDefault()
          setMentionIndex((prev) => (prev + step + combinedData.length) % combinedData.length)
          return
        }

        switch (e.key) {
          case 'Enter':
          case 'Tab': {
            e.stopImmediatePropagation()
            e.preventDefault()
            const mention = combinedData[mentionIndex]
            if (mention) {
              onSelectMention(mention)
            }
            break
          }
          case 'Escape':
            e.stopImmediatePropagation()
            closeMentions()
            break
          // Deleting or spacing past the trigger character means the user is
          // writing prose again, not narrowing a query.
          case 'Backspace':
          case ' ':
            if (mentionSearch === '') {
              closeMentions()
            }
            break
        }
      }
    },
    [mentionSearch, mentionIndex, setMentionIndex, closeMentions, onSelectMention],
  )
}
